import cx from 'classnames';
import * as _ from 'lodash';
import { useEffect, useRef, useState, type VoidFunctionComponent } from 'react';

import { useIsOverflow } from '../../../hooks/useIsOverflow';
import Button, { BUTTON_TYPE } from '../Button';

import styles from './DynamicShowMoreText.module.less';

type Props = {
  /**
   * The text to display.
   */
  text: string;
  /**
   * Whether to use the monospace font for the text. Defaults to true.
   */
  shouldUnsetFont?: boolean;
};

/**
 * Component which will render a block of text, and if it overflows
 * beyond 120px, it will render a "Show more" button to expand the text.
 *
 * If it is not overflowing, it will render the text as is, and the "Show more" button will not be shown.
 *
 * @param {Props} props - The props for the component.
 * @param {string} props.text - The text to display.
 * @param {boolean} props.shouldUnsetFont - By default there is a monospace font for the text. This prop will unset the font. Defaults to false.
 * @example
 * <DynamicShowMoreText text="This is a long piece of text that will overflow if it is too long." />
 */
export const DynamicShowMoreText: VoidFunctionComponent<Props> = ({ text, shouldUnsetFont = false }) => {
  const ref = useRef<HTMLDivElement>(null);
  const isOverflow = useIsOverflow(ref);

  const [isShowingDescription, setIsShowingDescription] = useState(false);

  const [isExpanded, setIsExpanded] = useState(false);

  useEffect(() => {
    setIsExpanded(!isOverflow);
  }, [isOverflow]);

  return (
    <>
      <div
        ref={ref}
        className={cx(styles.textBlock, {
          [styles.expanded]: isShowingDescription || isExpanded,
          [styles.textBlockUnsetFont]: shouldUnsetFont,
        })}>
        {!_.isEmpty(text) ? text : 'No description available.'}
      </div>
      {isOverflow && (
        <Button
          type={BUTTON_TYPE.ANCHOR}
          onClick={() => setIsShowingDescription(!isShowingDescription)}
      >
          <div className={styles.buttonText}>{isShowingDescription ? 'Show less' : 'Show all'}</div>
        </Button>
      )}
    </>
  );
};
